<template>
  <dx-modal
    id="modal-telemedicine"
    size="sm"
    title="Telemedicina"
    :value="show"
    @input="close"
  >
    <div class="telemedicine-card">
      <template v-if="active && meetingUrl">
        <div class="telemedicine-status">
          <fa-icon :icon="['fal', 'check-circle']" size="3x" class="text-success" />
          <p class="h5 text-success mt-2 mb-0">{{ statusLabel }}</p>
        </div>
        <div class="input-group mb-2">
          <input type="text" class="form-input" :value="meetingUrl" readonly>
          <button
            type="button"
            class="input-group-addon btn btn-icon tooltip tooltip-left"
            data-tooltip="Copiar link"
            @click="copyLink"
          ><fa-icon :icon="['fal', 'copy']" /></button>
          <button
            type="button"
            class="input-group-addon btn btn-icon tooltip-left"
            :class="{ loading: sending, tooltip: !sending }"
            :data-tooltip="whatsappTooltip"
            :disabled="!canSendLink || sending"
            @click="sendLink"
          ><fa-icon :icon="['fab', 'whatsapp']" /></button>
        </div>
        <p class="text-gray text-small">
          Envie o link ao paciente pelo WhatsApp ou copie para compartilhar
          por outro canal.
        </p>
        <div class="telemedicine-actions">
          <button
            type="button"
            class="btn btn-primary btn-icon btn-icon-left tooltip-top"
            :class="{ tooltip: opened }"
            :data-tooltip="openedTooltip"
            :disabled="opened"
            @click="openCallAndClose"
          ><fa-icon :icon="['fal', 'video']" /> Entrar na sala</button>
          <button
            type="button"
            class="btn btn-error btn-icon btn-icon-left tooltip tooltip-top"
            :data-tooltip="endTooltip"
            :class="{ loading: ending }"
            :disabled="ending"
            @click="end"
          ><fa-icon :icon="['fal', 'unlink']" /> Invalidar link</button>
        </div>
      </template>
      <div class="empty" v-else-if="googleNotConnected">
        <div class="empty-icon">
          <fa-icon :icon="['fab', 'google']" size="3x" />
        </div>
        <p class="empty-title h5">Conta Google não conectada</p>
        <p class="empty-subtitle">
          Conecte sua conta Google em Meu perfil para poder iniciar chamadas
          de telemedicina pelo Google Meet.
        </p>
        <div class="empty-action">
          <router-link
            to="/me"
            class="btn btn-primary btn-icon btn-icon-left"
            @click.native="close"
          ><fa-icon :icon="['fab', 'google']" /> Ir para Meu perfil</router-link>
        </div>
      </div>
      <div class="empty" v-else>
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'video']" size="3x" />
        </div>
        <p class="empty-title h5">Nenhuma chamada iniciada</p>
        <p class="empty-subtitle">
          Inicie uma chamada de vídeo pelo Google Meet para realizar o
          atendimento por telemedicina.
        </p>
        <div class="empty-action">
          <button
            type="button"
            class="btn btn-primary btn-icon btn-icon-left"
            :class="{ loading: starting }"
            :disabled="starting"
            @click="start"
          ><fa-icon :icon="['fal', 'video']" /> Iniciar chamada</button>
        </div>
      </div>
    </div>
    <template slot="footer">
      <button type="button" class="btn" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { isMetaEnabled, isWhatsappWebEnabled, whatsappText } from '@/helpers/telemedicine';

const END_TOOLTIP = 'Isso não encerra uma chamada já em andamento — '
  + 'para sair, feche a aba do Google Meet.';
const OPENED_TOOLTIP = 'A chamada já foi aberta em outra aba.';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    medicalRecordId: {
      type: String,
      required: true,
    },
    telemedicine: {
      type: Object,
      default: () => ({ active: false, meetingUrl: null }),
    },
    patient: {
      type: Object,
      default: () => ({ name: '', cellphone: '' }),
    },
    professionalName: {
      type: String,
      default: '',
    },
    date: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      active: this.telemedicine?.active || false,
      meetingUrl: this.telemedicine?.meetingUrl || null,
      opened: this.telemedicine?.opened || false,
      starting: false,
      ending: false,
      sending: false,
      googleNotConnected: false,
      endTooltip: END_TOOLTIP,
      openedTooltip: OPENED_TOOLTIP,
    };
  },
  computed: {
    statusLabel() {
      return this.opened ? 'Chamada em andamento' : 'Sala pronta para o atendimento';
    },
    canSendLink() {
      return !!this.meetingUrl
        && !!this.patient.cellphone
        && (isMetaEnabled() || isWhatsappWebEnabled());
    },
    whatsappTooltip() {
      if (!isMetaEnabled() && !isWhatsappWebEnabled()) {
        return 'Envio pelo WhatsApp não habilitado';
      }
      return this.patient.cellphone
        ? 'Enviar link pelo WhatsApp'
        : 'Paciente sem celular cadastrado';
    },
  },
  methods: {
    start() {
      this.starting = true;
      this.googleNotConnected = false;
      this.$httpX
        .post(`/medical-records/${this.medicalRecordId}/telemedicine`)
        .then(({ data }) => {
          this.active = data.active;
          this.meetingUrl = data.meetingUrl;
          this.opened = false;
          this.emitUpdate();
        })
        .catch((e) => {
          if (e.response?.data?.reason === 'googleNotConnected') {
            this.googleNotConnected = true;
            return;
          }
          this.$toast.error(e);
        })
        .finally(() => {
          this.starting = false;
        });
    },
    openCall() {
      if (this.meetingUrl) {
        window.open(this.meetingUrl, '_blank');
        this.opened = true;
        this.emitUpdate();
      }
    },
    openCallAndClose() {
      this.openCall();
      this.close();
    },
    copyLink() {
      if (!this.meetingUrl) {
        return;
      }
      navigator.clipboard
        .writeText(this.meetingUrl)
        .then(() => this.$toast.success('Link copiado!'))
        .catch(() => this.$toast.show('Não foi possível copiar o link', { type: 'error' }));
    },
    async sendLink() {
      if (!isMetaEnabled()) {
        this.openWhatsappWeb();
        return;
      }

      this.sending = true;

      try {
        await this.$httpX
          .post(`/medical-records/${this.medicalRecordId}/telemedicine/message`);
        this.$toast.success('Link enviado pelo WhatsApp');
      } catch (e) {
        const reason = e.response && e.response.data ? e.response.data.reason : null;

        if (['templateNotApproved', 'whatsappNotEnabled'].includes(reason)) {
          this.$toast.show(e.response.data.message, { type: 'warning' });
          this.openWhatsappWeb();
        } else {
          this.$toast.error(e);
        }
      } finally {
        this.sending = false;
      }
    },
    openWhatsappWeb() {
      this.$whatsappWeb.open(
        this.patient.cellphone,
        whatsappText({
          patientName: this.patient.name,
          professionalName: this.professionalName,
          date: this.date || new Date(),
          meetingUrl: this.meetingUrl,
        }),
      );
    },
    end() {
      this.ending = true;
      this.$httpX
        .delete(`/medical-records/${this.medicalRecordId}/telemedicine`)
        .then(() => {
          this.active = false;
          this.meetingUrl = null;
          this.opened = false;
          this.emitUpdate();
        })
        .catch(this.$toast.error)
        .finally(() => {
          this.ending = false;
        });
    },
    emitUpdate() {
      this.$emit('update', {
        active: this.active,
        meetingUrl: this.meetingUrl,
        opened: this.opened,
      });
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.telemedicine-card {
  .telemedicine-status {
    margin-bottom: $layout-spacing-lg;
    text-align: center;
  }

  .telemedicine-actions {
    display: flex;
    gap: $layout-spacing;
  }

  .input-group {
    .form-input {
      background-color: $gray-color-ultra-light;
    }
  }
}
</style>
